/**
 * 個別設定 (override) 編集ダイアログ (v3.2.18)。
 *
 * - 新規: SA / 社員を select で選択 → items + briefingFee を編集 → 保存
 * - 編集: 既存 override の items + briefingFee + memo を編集
 * - CSV: 商品テーブルの CSV 出力 / 一括入力 (列構成は CommissionDetailPage と統一、briefingFee は UI 入力)
 *
 * items 編集はカテゴリー横断で全商品を表示する単純なテーブル形式 (Phase 2 最小実装)。
 * 将来的にはカテゴリー別エディタ (CommissionDetailPage) と統合する想定。
 *
 * v3.2.18 で保存時の items shape を CommissionItem 必須フィールドのみに厳格化 +
 * HttpsError 詳細 (code/details/payload) を console.error にダンプ。
 *
 * 2026-11-02: SA の開拓顧客加算率を個別に設定できるようにした。
 *   - 「開拓顧客加算率」欄 = この SA の既定 (空欄 = セットのカテゴリー別の標準 / 0 = 加算なし)
 *   - 商品表の「加算率」列 = 商品ごとの指定 (空欄 = 上の既定を引き継ぐ / 0 = 加算なし)
 *   - CSV は SA のとき selfDevelopedAdditionRate 列を足す (列が無い CSV は加算率を変えない)
 */
import type { CommissionItem, CommissionOverride, CommissionType } from '@goenhub/shared';
import {
  normalizeSelfDevelopedAdditionRate,
  resolveEffectiveSelfDevelopedAdditionRate,
  resolveSelfDevelopedAdditionRate,
} from '@goenhub/shared';
import { doc, getDoc } from 'firebase/firestore';
import { Download, Loader2, Save, Upload } from 'lucide-react';
import Papa from 'papaparse';
import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';

import { buildProductIdToCategoryId } from './categoryCatalogLink';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Spinner } from '@/components/ui/spinner';
import { useUpsertCommissionOverride } from '@/hooks/useCommissionOverrides';
import { useCommissionSetDetail } from '@/hooks/useCommissions';
import { useCatalogItemsForCommissionSet } from '@/hooks/useProductCatalogs';
import { useProductCategories } from '@/hooks/useProductCategories';
import { db } from '@/lib/firebase';

/** 加算率の入力欄の文字列 → 数値 (空欄は null)。 */
function parseRateInput(raw: string): number | null {
  const t = raw.trim();
  if (t === '') return null;
  return Number(t);
}

const isValidRate = (v: number | null | undefined): boolean =>
  v == null || (Number.isFinite(v) && v >= 0 && v <= 100);

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  setId: string;
  /** 編集対象 (null = 新規) */
  editing: CommissionOverride | null;
  eligibleAdmins: Array<{
    id: string;
    fullName?: string;
    displayName?: string;
    email?: string;
    level?: string;
  }>;
  /** 既に override がある uid セット (新規時に重複防止) */
  existingIds: Set<string>;
}

export function CommissionOverrideDialog({
  open,
  onOpenChange,
  setId,
  editing,
  eligibleAdmins,
  existingIds,
}: Props) {
  const upsert = useUpsertCommissionOverride();
  // v3.2.30+: 季節モードではなく、このセットに紐付くカタログの価格を参照する
  const allItems = useCatalogItemsForCommissionSet(open ? setId : null);
  // 開拓顧客加算率の「セットの標準」(カテゴリー別) を引くため
  const setDetail = useCommissionSetDetail(open ? setId : null);
  const categories = useProductCategories();

  const [recipientId, setRecipientId] = useState<string>('');
  const [defaultType, setDefaultType] = useState<CommissionType>('rate');
  const [briefingFee, setBriefingFee] = useState<string>('0');
  /** SA の開拓顧客加算率の既定。空欄 = セットの標準 */
  const [additionRate, setAdditionRate] = useState<string>('');
  const [memo, setMemo] = useState<string>('');
  const [items, setItems] = useState<CommissionItem[]>([]);

  // 編集モード時は初期値をセット
  useEffect(() => {
    if (open && editing) {
      setRecipientId(editing.recipientId);
      setDefaultType(editing.defaultType);
      setBriefingFee(String(editing.briefingFee ?? 0));
      setAdditionRate(
        typeof editing.selfDevelopedAdditionRate === 'number'
          ? String(editing.selfDevelopedAdditionRate)
          : '',
      );
      setMemo(editing.memo ?? '');
      setItems(editing.items);
    } else if (open && !editing) {
      // 新規: 最初の eligible を初期選択
      const first = eligibleAdmins.find((a) => !existingIds.has(a.id));
      setRecipientId(first?.id ?? '');
      setDefaultType('rate');
      setBriefingFee('0');
      setAdditionRate('');
      setMemo('');
      setItems([]);
    }
  }, [open, editing, eligibleAdmins, existingIds]);

  // 開拓顧客加算は SA だけ (社員は対象外)
  const isSa = editing
    ? editing.recipientType === 'sa'
    : eligibleAdmins.find((a) => a.id === recipientId)?.level === 'external';

  const setForRates = setDetail.data?.set ?? null;
  const productIdToCategoryId = useMemo(
    () => buildProductIdToCategoryId(allItems.data ?? [], categories.data ?? []),
    [allItems.data, categories.data],
  );
  const parsedAdditionRate = parseRateInput(additionRate);
  const additionRateValid = isValidRate(parsedAdditionRate);

  /** 「セットの標準」の表示用: このセットの商品が属するカテゴリーごとの加算率 */
  const standardRatesLabel = useMemo(() => {
    const catIds = Array.from(new Set(productIdToCategoryId.values()));
    const cats = categories.data ?? [];
    const parts = catIds
      .map((id) => {
        const c = cats.find((x) => x.id === id);
        return {
          name: c?.name ?? id,
          order: c?.displayOrder ?? 0,
          rate: resolveSelfDevelopedAdditionRate(setForRates, id),
        };
      })
      .sort((a, b) => a.order - b.order)
      .map((x) => `${x.name} +${x.rate}%`);
    return parts.length > 0 ? parts.join(' / ') : 'カテゴリー別';
  }, [productIdToCategoryId, categories.data, setForRates]);

  /** 商品の「加算率」欄が空欄のときに使われる率 (入力欄の薄字に出す) */
  const inheritedRateFor = (productId: string): number =>
    resolveEffectiveSelfDevelopedAdditionRate({
      set: setForRates,
      categoryId: productIdToCategoryId.get(productId) ?? null,
      overrideRate: additionRateValid ? parsedAdditionRate : null,
    }).rate;

  // 全商品をベースにした編集用 items リスト (空欄補完)
  const editableItems = useMemo(() => {
    const products = allItems.data ?? [];
    const byProductId = new Map<string, CommissionItem>();
    for (const it of items) byProductId.set(it.productId, it);
    return products.map((p) => {
      const existing = byProductId.get(p.id);
      return {
        productId: p.id,
        catalogId: p.catalogId,
        productName: p.name,
        priceExcludingTax: p.priceExcludingTax,
        value: existing?.value ?? 0,
        type: existing?.type ?? null,
        additionalAmount: existing?.additionalAmount ?? 0,
        discountAmount: existing?.discountAmount ?? 0,
        selfDevelopedAdditionRate: existing?.selfDevelopedAdditionRate ?? null,
      };
    });
  }, [allItems.data, items]);

  const updateItem = (productId: string, patch: Partial<CommissionItem>) => {
    setItems((prev) => {
      const idx = prev.findIndex((it) => it.productId === productId);
      const product = (allItems.data ?? []).find((p) => p.id === productId);
      if (!product) return prev;
      const base: CommissionItem = {
        productId,
        catalogId: product.catalogId,
        productName: product.name,
        type: null,
        value: 0,
        additionalAmount: 0,
        discountAmount: 0,
      };
      if (idx === -1) return [...prev, { ...base, ...patch }];
      const next = [...prev];
      next[idx] = { ...next[idx], ...patch };
      return next;
    });
  };

  const handleSave = async () => {
    if (!recipientId) {
      toast.error('受取人を選択してください');
      return;
    }
    if (isSa) {
      if (!additionRateValid) {
        toast.error('開拓顧客加算率は 0〜100 の範囲で入力してください (空欄 = セットの標準)');
        return;
      }
      const badRow = items.find(
        (it) => Number(it.value) > 0 && !isValidRate(it.selfDevelopedAdditionRate),
      );
      if (badRow) {
        toast.error(`「${badRow.productName}」の加算率は 0〜100 の範囲で入力してください`);
        return;
      }
    }
    try {
      // v3.2.18: 保存前に admins/{recipientId} の存在確認 (pending 残骸 / 削除済みの早期検知)。
      // 失敗時の HttpsError('not-found') よりユーザフレンドリなメッセージで停止する。
      const adminSnap = await getDoc(doc(db, 'admins', recipientId));
      if (!adminSnap.exists()) {
        toast.error(
          `受取人の管理者ドキュメントが存在しません (id: ${recipientId})。\n対象ユーザーが未ログイン (招待中) の可能性があります。先にログインしてもらうか、ユーザー管理画面で状態を確認してください。`,
        );
        return;
      }

      // 明示的に CommissionItem の必須フィールドだけを構築（priceExcludingTax 等の混入防止）。
      // value > 0 のものだけ送信 (size 削減)
      const itemsToSend = items
        .filter((it) => it && it.productId && it.catalogId && Number(it.value) > 0)
        .map<CommissionItem>((it) => ({
          productId: String(it.productId),
          catalogId: String(it.catalogId),
          productName: String(it.productName ?? ''),
          type: it.type === 'rate' || it.type === 'fixed' ? it.type : null,
          value: Number.isFinite(Number(it.value)) ? Math.max(0, Number(it.value)) : 0,
          additionalAmount: Number.isFinite(Number(it.additionalAmount))
            ? Math.max(0, Math.round(Number(it.additionalAmount)))
            : 0,
          discountAmount: Number.isFinite(Number(it.discountAmount))
            ? Math.max(0, Math.round(Number(it.discountAmount)))
            : 0,
          // 空欄も null で必ず送る (キーが無いとサーバーは保存済みの値を残す)
          selfDevelopedAdditionRate: isSa
            ? normalizeSelfDevelopedAdditionRate(it.selfDevelopedAdditionRate)
            : null,
        }));
      await upsert.mutateAsync({
        setId,
        recipientId,
        defaultType,
        items: itemsToSend,
        briefingFee: Number(briefingFee) || 0,
        selfDevelopedAdditionRate: isSa
          ? normalizeSelfDevelopedAdditionRate(parsedAdditionRate)
          : null,
        memo: memo.trim() || null,
      });
      toast.success(editing ? '個別設定を更新しました' : '個別設定を追加しました');
      onOpenChange(false);
    } catch (err) {
      // HttpsError は code/message/details を持つ。原因特定のため全部出す。
      const e = err as { code?: string; message?: string; details?: unknown };
      const code = e?.code ? `[${e.code}] ` : '';
      const msg = e?.message ?? String(err);
      // eslint-disable-next-line no-console
      console.error('[upsertCommissionOverride] failed', {
        code: e?.code,
        message: e?.message,
        details: e?.details,
        payload: {
          setId,
          recipientId,
          defaultType,
          itemCount: items.filter((it) => Number(it.value) > 0).length,
          briefingFee: Number(briefingFee) || 0,
        },
      });
      toast.error(`失敗: ${code}${msg}`);
    }
  };

  // ─────────────────────────────────────────────
  // CSV エクスポート / インポート (Phase B-2 / v3.2.18)
  // CommissionDetailPage と同列構成: productId, sku, productName,
  //  priceExcludingTax, type, value, additionalAmount, discountAmount
  // briefingFee は UI 入力 (CSV には含めない)
  // ─────────────────────────────────────────────
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const handleExportCsv = () => {
    const headers = [
      'productId',
      'sku',
      'productName',
      'priceExcludingTax',
      'type',
      'value',
      'additionalAmount',
      'discountAmount',
      // SA のみ: 開拓顧客加算率 (空欄 = 既定を引き継ぐ / 0 = 加算なし)
      ...(isSa ? ['selfDevelopedAdditionRate'] : []),
    ];
    const rows = editableItems.map((it) => {
      const meta = (allItems.data ?? []).find((p) => p.id === it.productId);
      return [
        it.productId,
        meta?.sku ?? '',
        it.productName,
        it.priceExcludingTax ?? '',
        it.type === 'rate' ? '%' : it.type === 'fixed' ? '円' : '',
        it.value,
        it.additionalAmount,
        it.discountAmount,
        ...(isSa ? [it.selfDevelopedAdditionRate ?? ''] : []),
      ];
    });
    const csv = Papa.unparse({ fields: headers, data: rows });
    // Excel 文字化け防止のため UTF-8 BOM を付与
    const blob = new Blob(['﻿', csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const stamp = new Date().toISOString().replace(/[:T]/g, '-').slice(0, 16);
    const recipientLabel =
      eligibleAdmins.find((a) => a.id === recipientId)?.fullName ??
      eligibleAdmins.find((a) => a.id === recipientId)?.displayName ??
      recipientId ??
      'override';
    const safe = (s: string) => s.replace(/[\s/\\?%*:|"<>]/g, '_').slice(0, 40);
    const a = document.createElement('a');
    a.href = url;
    a.download = `commission_override_${safe(recipientLabel)}_${stamp}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success(`${editableItems.length} 件をテンプレート出力しました`);
  };

  const handleImportCsv = (file: File) => {
    const products = allItems.data ?? [];
    if (products.length === 0) {
      toast.error('商品マスタの読み込みが完了していません。しばらくしてから再度お試しください。');
      return;
    }
    const productById = new Map(products.map((p) => [p.id, p]));
    // 別セット(別カタログ)出力の CSV や、カタログ差替え後の古いテンプレートは
    // productId が一致しないため、不変キーである SKU で照合フォールバックする
    // (SKU 重複時は照合が曖昧になるため null で無効化)
    const productBySku = new Map<string, (typeof products)[number] | null>();
    for (const p of products) {
      const sku = (p.sku ?? '').trim();
      if (!sku) continue;
      productBySku.set(sku, productBySku.has(sku) ? null : p);
    }

    Papa.parse<Record<string, string>>(file, {
      header: true,
      skipEmptyLines: true,
      complete: (result) => {
        const rows = result.data ?? [];
        // 加算率の列がある CSV のときだけ加算率を読み込む (古いテンプレートでは今の値を残す)
        const hasAdditionRateColumn =
          isSa && (result.meta.fields ?? []).includes('selfDevelopedAdditionRate');
        // 既存 items を Map 化（編集前の状態を保持）
        const byId = new Map<string, CommissionItem>(items.map((it) => [it.productId, it]));
        let updated = 0;
        let matchedBySku = 0;
        let skippedUnknown = 0;
        const invalidLines: string[] = [];

        rows.forEach((row, idx) => {
          const productId = (row.productId ?? '').trim();
          const sku = (row.sku ?? '').trim();
          if (!productId && !sku) return;
          let product = productId ? productById.get(productId) : undefined;
          if (!product && sku) {
            const bySku = productBySku.get(sku);
            if (bySku) {
              product = bySku;
              matchedBySku += 1;
            }
          }
          if (!product) {
            skippedUnknown += 1;
            return;
          }

          const rawType = (row.type ?? '').trim();
          let typeVal: CommissionType | null;
          if (rawType === '' || rawType === '既定') {
            typeVal = null;
          } else if (rawType === 'rate' || rawType === '%') {
            typeVal = 'rate';
          } else if (rawType === 'fixed' || rawType === '円') {
            typeVal = 'fixed';
          } else {
            invalidLines.push(`行${idx + 2}: type "${rawType}" が不正`);
            return;
          }

          const toNum = (v: string | undefined): number => {
            if (v == null || v === '') return 0;
            const n = Number(String(v).replace(/,/g, ''));
            return Number.isFinite(n) ? n : 0;
          };

          let additionRateVal: number | null | undefined;
          if (hasAdditionRateColumn) {
            const raw = (row.selfDevelopedAdditionRate ?? '').trim().replace(/%$/, '');
            additionRateVal = raw === '' ? null : Number(raw);
            if (!isValidRate(additionRateVal)) {
              invalidLines.push(
                `行${idx + 2}: selfDevelopedAdditionRate "${row.selfDevelopedAdditionRate}" は 0〜100 で入力`,
              );
              return;
            }
          }

          // 既存があれば値だけ上書き、無ければ base から構築。
          // SKU 照合の場合 CSV の productId は別カタログの ID なので、
          // 必ず現在カタログ側の product.id をキーに更新する
          const base = byId.get(product.id) ?? {
            productId: product.id,
            catalogId: product.catalogId,
            productName: product.name,
            type: null,
            value: 0,
            additionalAmount: 0,
            discountAmount: 0,
          };
          byId.set(product.id, {
            ...base,
            // productId / catalogId / productName は常に最新マスタから補完（CSV の値より優先）
            productId: product.id,
            catalogId: product.catalogId,
            productName: product.name,
            type: typeVal,
            value: toNum(row.value),
            additionalAmount: toNum(row.additionalAmount),
            discountAmount: toNum(row.discountAmount),
            ...(additionRateVal !== undefined
              ? { selfDevelopedAdditionRate: additionRateVal }
              : {}),
          });
          updated += 1;
        });

        // CSV に出てこなかった既存 items はそのまま残す
        setItems(Array.from(byId.values()));

        const messages: string[] = [`${updated} 件を反映しました`];
        if (matchedBySku > 0) messages.push(`うち ${matchedBySku} 行は SKU で照合`);
        if (skippedUnknown > 0) messages.push(`未登録商品 ${skippedUnknown} 行をスキップ`);
        if (invalidLines.length > 0) messages.push(`不正 ${invalidLines.length} 行`);
        toast.success(messages.join(' / '));
        if (updated === 0 && skippedUnknown > 0) {
          toast.warning(
            '全行がこのセットのカタログと一致しませんでした。別の報酬セットで出力した CSV か、カタログ差替え前の古いテンプレートの可能性があります。このダイアログの「CSV出力」から出し直すか、SKU 列が現在のカタログと一致しているか確認してください。',
          );
        }
        if (invalidLines.length > 0) {
          toast.warning(invalidLines.slice(0, 5).join('\n'));
        }
        toast.info('「保存」ボタンで確定してください');
      },
      error: (err) => {
        toast.error(`CSV読込に失敗: ${err.message}`);
      },
    });
  };

  // 商品表の列幅 (SA は「加算率」列を足す)
  const rowGrid = isSa
    ? 'minmax(0,2fr) 70px 80px 80px 80px 72px'
    : 'minmax(0,2fr) 70px 80px 80px 80px';

  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleImportCsv(file);
    e.target.value = '';
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {editing ? `個別設定を編集: ${editing.recipientName}` : '個別設定を追加'}
          </DialogTitle>
          <DialogDescription>
            この受取人にロール標準と異なる報酬テーブル + 個別説明代行料 (SA は開拓顧客加算率も)
            を設定します。
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* 受取人 */}
          {!editing && (
            <div>
              <label className="block text-xs text-[var(--text-muted)] mb-1">受取人</label>
              <select
                value={recipientId}
                onChange={(e) => setRecipientId(e.target.value)}
                className="w-full px-3 py-2 text-sm rounded-sm border border-[var(--border)] bg-[var(--bg-input)]"
              >
                <option value="">選択してください</option>
                {eligibleAdmins.map((a) => (
                  <option key={a.id} value={a.id} disabled={existingIds.has(a.id)}>
                    {a.fullName ?? a.displayName ?? a.email}（
                    {a.level === 'external' ? 'SA' : '社員'}）
                    {existingIds.has(a.id) ? ' (設定済み)' : ''}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* 既定の報酬タイプ */}
          <div>
            <label className="block text-xs text-[var(--text-muted)] mb-1">既定の報酬タイプ</label>
            <div className="flex gap-2">
              {(['rate', 'fixed'] as CommissionType[]).map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => setDefaultType(t)}
                  className={`px-3 py-1.5 text-xs rounded border ${
                    defaultType === t
                      ? 'border-[var(--accent)] bg-[var(--accent-subtle)] text-[var(--accent)]'
                      : 'border-[var(--border)] text-[var(--text-secondary)] hover:bg-[var(--bg-hover)]'
                  }`}
                >
                  {t === 'rate' ? 'レート (%)' : '固定額 (円)'}
                </button>
              ))}
            </div>
          </div>

          {/* 個別説明代行料 */}
          <div>
            <label className="block text-xs text-[var(--text-muted)] mb-1">
              個別説明代行料 (1 件あたり、円)
            </label>
            <input
              type="number"
              min={0}
              step={100}
              value={briefingFee}
              onChange={(e) => setBriefingFee(e.target.value)}
              className="w-full px-3 py-2 text-sm rounded-sm border border-[var(--border)] bg-[var(--bg-input)] tabular-nums"
            />
            <p className="text-[10px] text-[var(--text-muted)] mt-1">
              0 = 設定なし。値があると SA 請求書発行時に未請求の個別説明件数 ×
              この金額が自動加算されます。
            </p>
          </div>

          {/* 開拓顧客加算率 (SA のみ) */}
          {isSa && (
            <div className="rounded-md border border-emerald-300/40 bg-emerald-50/30 dark:bg-emerald-950/10 p-3">
              <label className="block text-xs text-[var(--text-muted)] mb-1">
                開拓顧客加算率 (%)
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min={0}
                  max={100}
                  step="0.1"
                  value={additionRate}
                  onChange={(e) => setAdditionRate(e.target.value)}
                  placeholder="空欄 = セットの標準"
                  aria-invalid={!additionRateValid}
                  className="w-48 px-3 py-2 text-sm rounded-sm border border-[var(--border)] bg-[var(--bg-input)] tabular-nums"
                />
                <span className="text-xs">%</span>
                {additionRate.trim() !== '' && (
                  <button
                    type="button"
                    onClick={() => setAdditionRate('')}
                    className="text-[11px] text-[var(--accent)] hover:underline"
                  >
                    セットの標準に戻す
                  </button>
                )}
              </div>
              <p className="text-[10px] text-[var(--text-muted)] mt-1">
                空欄 = セットの標準 ({standardRatesLabel}) を使います。0 を入れると、この SA
                は開拓顧客でも加算なし。 商品ごとに変える場合は下の表の「加算率」に入力します
                (空欄の商品はここの値を使います)。 弊社紹介の商談と固定額 (円)
                の商品には加算しません。
              </p>
              {!additionRateValid && (
                <p className="text-[10px] text-[var(--danger,_#d33)] mt-1">
                  0〜100 の範囲で入力してください
                </p>
              )}
            </div>
          )}

          {/* メモ */}
          <div>
            <label className="block text-xs text-[var(--text-muted)] mb-1">メモ (任意)</label>
            <input
              type="text"
              value={memo}
              onChange={(e) => setMemo(e.target.value)}
              placeholder="例: 2026年度特別契約"
              className="w-full px-3 py-2 text-sm rounded-sm border border-[var(--border)] bg-[var(--bg-input)]"
            />
          </div>

          {/* 商品報酬テーブル */}
          <div>
            <div className="flex items-center justify-between mb-1 gap-2 flex-wrap">
              <label className="block text-xs text-[var(--text-muted)]">
                商品別報酬 ({editableItems.length} 件 / 値 0 は無視されます)
              </label>
              <div className="flex items-center gap-1">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={handleExportCsv}
                  disabled={allItems.isLoading}
                  title="現在の値を CSV テンプレートとして出力"
                >
                  <Download className="size-3.5" /> CSV出力
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={allItems.isLoading}
                  title="CSV を一括取込（保存ボタンで確定）"
                >
                  <Upload className="size-3.5" /> CSV一括入力
                </Button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv,text/csv"
                  onChange={onFileChange}
                  className="hidden"
                />
              </div>
            </div>
            {allItems.isLoading ? (
              <Spinner />
            ) : (
              <div className="rounded-md border border-[var(--border)] overflow-hidden max-h-[400px] overflow-y-auto">
                <div
                  className="grid gap-2 px-3 py-1.5 bg-[var(--bg-elevated)] text-[10px] uppercase tracking-wider text-[var(--text-muted)] sticky top-0"
                  style={{ gridTemplateColumns: rowGrid }}
                >
                  <div>商品</div>
                  <div>タイプ</div>
                  <div className="text-right">値</div>
                  <div className="text-right">追加</div>
                  <div className="text-right">割引</div>
                  {isSa && (
                    <div
                      className="text-right"
                      title="開拓顧客加算率 (%)。空欄 = 上の既定 (薄字の値) を使う / 0 = この商品は加算なし"
                    >
                      加算率
                    </div>
                  )}
                </div>
                {editableItems.map((it) => (
                  <div
                    key={it.productId}
                    className="grid gap-2 px-3 py-1 text-xs border-t border-[var(--border)] items-center"
                    style={{ gridTemplateColumns: rowGrid }}
                  >
                    <div className="truncate">
                      <div className="font-medium">{it.productName}</div>
                      <div className="text-[10px] text-[var(--text-muted)]">
                        ¥{it.priceExcludingTax.toLocaleString()}
                      </div>
                    </div>
                    <select
                      value={it.type ?? ''}
                      onChange={(e) =>
                        updateItem(it.productId, {
                          type: (e.target.value || null) as CommissionItem['type'],
                        })
                      }
                      className="px-1 py-0.5 text-[10px] rounded-sm border border-[var(--border)] bg-[var(--bg-input)]"
                    >
                      <option value="">既定</option>
                      <option value="rate">%</option>
                      <option value="fixed">円</option>
                    </select>
                    <input
                      type="number"
                      min={0}
                      step="0.1"
                      value={it.value}
                      onChange={(e) =>
                        updateItem(it.productId, { value: Number(e.target.value) || 0 })
                      }
                      className="px-1 py-0.5 text-[10px] rounded-sm border border-[var(--border)] bg-[var(--bg-input)] tabular-nums text-right"
                    />
                    <input
                      type="number"
                      min={0}
                      step={100}
                      value={it.additionalAmount}
                      onChange={(e) =>
                        updateItem(it.productId, { additionalAmount: Number(e.target.value) || 0 })
                      }
                      className="px-1 py-0.5 text-[10px] rounded-sm border border-[var(--border)] bg-[var(--bg-input)] tabular-nums text-right"
                    />
                    <input
                      type="number"
                      min={0}
                      step={100}
                      value={it.discountAmount}
                      onChange={(e) =>
                        updateItem(it.productId, { discountAmount: Number(e.target.value) || 0 })
                      }
                      className="px-1 py-0.5 text-[10px] rounded-sm border border-[var(--border)] bg-[var(--bg-input)] tabular-nums text-right"
                    />
                    {isSa &&
                      ((it.type ?? defaultType) === 'fixed' ? (
                        <div
                          className="text-right text-[10px] text-[var(--text-muted)]"
                          title="固定額 (円) の商品は開拓顧客加算の対象外"
                        >
                          対象外
                        </div>
                      ) : (
                        <input
                          type="number"
                          min={0}
                          max={100}
                          step="0.1"
                          value={it.selfDevelopedAdditionRate ?? ''}
                          placeholder={String(inheritedRateFor(it.productId))}
                          title="空欄 = 上の既定を使う (薄字の値) / 0 = 加算なし"
                          aria-label={`${it.productName} の開拓顧客加算率`}
                          onChange={(e) =>
                            updateItem(it.productId, {
                              selfDevelopedAdditionRate: parseRateInput(e.target.value),
                            })
                          }
                          className={`px-1 py-0.5 text-[10px] rounded-sm border bg-[var(--bg-input)] tabular-nums text-right placeholder:text-[var(--text-muted)] placeholder:opacity-60 ${
                            isValidRate(it.selfDevelopedAdditionRate)
                              ? 'border-[var(--border)]'
                              : 'border-[var(--danger,_#d33)]'
                          }`}
                        />
                      ))}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
            キャンセル
          </Button>
          <Button type="button" variant="primary" onClick={handleSave} disabled={upsert.isPending}>
            {upsert.isPending ? (
              <Loader2 className="size-3.5 animate-spin" />
            ) : (
              <Save className="size-3.5" />
            )}
            保存
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
